import { useState } from 'react'
import { CalendarDays, ChevronDown, Lock, Check, RefreshCw } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/utils/cn'
import { Dialog } from '@/components/ui/Dialog'
import { donemApi, type Donem, type DonemDurum } from '@/services/modules/donem.api'
import { useUIStore } from '@/stores/ui.store'
import { useTabsStore } from '@/stores/tabs.store'

const isTauri = typeof window !== 'undefined' && !!(window as { __TAURI__?: unknown }).__TAURI__

const durumRenk: Record<DonemDurum, string> = {
  Acik: 'text-success',
  Devredildi: 'text-info',
  Kapali: 'text-error',
}
const durumEtiket: Record<DonemDurum, string> = {
  Acik: 'Açık',
  Devredildi: 'Devredildi',
  Kapali: 'Kapalı',
}

export function DonemSwitcher() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const notify = useUIStore((s) => s.notify)

  const { data: aktif } = useQuery({ queryKey: ['donem-aktif'], queryFn: donemApi.aktif })
  const { data: liste = [] } = useQuery({ queryKey: ['donem-liste'], queryFn: donemApi.liste })
  const [pending, setPending] = useState<Donem | null>(null)

  const aktifYapMut = useMutation({
    mutationFn: (id: string) => donemApi.aktifYap(id),
    onSuccess: (d: Donem) => {
      // Dönem değişti → tüm veri sorguları döneme bağlı; önbelleği tamamen temizle
      queryClient.clear()
      notify(`Aktif dönem: ${d.ad}`, 'success')
      setPending(null)
      if (isTauri) useTabsStore.getState().reset('/dashboard', 'Ana Sayfa')
      else navigate('/dashboard')
    },
    onError: () => { setPending(null); notify('Dönem değiştirilemedi', 'error') },
  })

  if (!aktif && liste.length === 0) return null

  const handleSelect = (d: Donem) => {
    if (d.id === aktif?.id) return
    setPending(d) // direkt geçme — önce onay iste
  }

  return (
    <>
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md',
            'bg-bg-elevated border text-sm transition-colors',
            aktif?.durum === 'Kapali'
              ? 'border-error/40 text-error hover:text-error'
              : 'border-bg-border text-text-secondary hover:text-text-primary',
            aktifYapMut.isPending && 'opacity-60 pointer-events-none',
          )}
          title="Çalışma Dönemi"
        >
          {aktif?.durum === 'Kapali' ? <Lock className="w-3.5 h-3.5" /> : <CalendarDays className="w-3.5 h-3.5" />}
          <span>{aktif ? aktif.yil : 'Dönem'}</span>
          {aktifYapMut.isPending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="min-w-56 bg-bg-surface border border-bg-border rounded-lg shadow-dropdown p-1 z-50 animate-slide-in-up"
        >
          <div className="px-3 py-2 border-b border-bg-border mb-1">
            <p className="text-xs text-text-muted">Çalışma Dönemi</p>
          </div>

          {liste.map((d) => (
            <DropdownMenu.Item
              key={d.id}
              onSelect={() => handleSelect(d)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer outline-none',
                d.id === aktif?.id
                  ? 'text-primary bg-primary/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
              )}
            >
              {d.id === aktif?.id ? (
                <Check className="w-3.5 h-3.5 text-primary shrink-0" />
              ) : (
                <div className="w-3.5 h-3.5 shrink-0" />
              )}
              <span className="flex-1">{d.ad}</span>
              <span className={cn('text-[11px]', durumRenk[d.durum])}>{durumEtiket[d.durum]}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>

    {/* Dönem geçiş onay dialogu */}
    <Dialog open={pending !== null} onClose={() => setPending(null)} title="Dönem Değiştir">
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">
          Aktif çalışma dönemini <span className="font-semibold text-text-primary">{pending?.ad}</span> olarak değiştirmek istediğinize emin misiniz?
        </p>
        {pending?.durum === 'Kapali' && (
          <p className="text-xs text-error">Bu dönem kapalı — kayıt ekleme/düzenleme yapılamaz (salt-okunur).</p>
        )}
        <p className="text-xs text-text-muted">
          Tüm ekranlar seçilen döneme göre yeniden yüklenecek ve ana sayfaya yönlendirileceksiniz.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={() => setPending(null)}
            className="px-4 py-2 text-sm rounded-md border border-bg-border text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            İptal
          </button>
          <button
            onClick={() => pending && aktifYapMut.mutate(pending.id)}
            disabled={aktifYapMut.isPending}
            className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors"
          >
            {aktifYapMut.isPending ? 'Geçiliyor…' : 'Evet, Geç'}
          </button>
        </div>
      </div>
    </Dialog>
    </>
  )
}
